import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Download } from "lucide-react";

import { describeApiError, isApiError } from "../../api/errors";
import {
  useCreateFakeConnection,
  useStartFreeAgentAuthorization,
  useStartLuciBookAuthorization,
  useStartQboAuthorization,
  useStartSageBcAuthorization,
  useStartXeroAuthorization,
  useSystems,
} from "../../api/hooks";
import { Badge } from "../../components/Badge";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { Page } from "../../components/Page";
import { VendorMark } from "../../components/VendorMark";
import { useToast } from "../../components/Toast";
import { t } from "../../i18n/t";
import { saveDraft } from "./newMigrationDraft";

/**
 * §P2, redesigned as AddConnection.html. The screen a Fatih-shaped question ("nasıl xerodan lucibooka
 * migration işlemini yapacak kullanıcı?") is the answer to: `/connections` listed connections but never
 * offered a way to make one. This is that way, reachable from the "Add connection" button on `/connections`.
 *
 * **Layout.** A back link, the title and the intro; then the `Accounting systems` section: one `.conn` tile
 * per system — a 44 px vendor mark, the name, one line of what connecting it does, and a single `Connect`
 * action (the full "Connect to Xero" name stays on the button's `aria-label`). Order is deliberate: the live
 * OAuth connectors first (LuciBook, Xero, QuickBooks Online, FreeAgent, Sage Business Cloud), then the ones
 * that cannot complete here (Sage 50's agent, HMRC's coming-soon tile). The dev-only fake connector never
 * sits in the grid at all; it gets the striped developer panel below it, gated on the server's `dev_mode`.
 *
 * **Xero** reuses the exact OAuth handoff `ConnectScreen` already drives (`start_xero_authorization` →
 * `window.location.assign` → `/oauth/xero/return` → tenant select), because that flow is the one already
 * accepted end to end. The only new thing is `return_to: "/connections"` in the pre-redirect draft (§B1.5's
 * allow-list already carries `return_to`; `XeroReturn` reads it to come back here instead of the migration
 * wizard when there is no destination connection in flight).
 *
 * **Sage 50** has no API connection to start: it needs the LuciBridge Windows agent installed next to the
 * desktop database, so its tile keeps the download action disabled with the note that the installer link is
 * coming, rather than pretending a button here can connect it.
 *
 * **QuickBooks Online / FreeAgent / Sage Business Cloud / LuciBook** each have a real
 * `POST /connections/{system}/authorize` entry point with the same same-tab `window.location.assign` handoff
 * Xero's tile uses. None of the four needs a `saveDraft`/`return_to` for itself (Xero's draft is for Xero's
 * tenant-picker return): their callbacks build the `Connection` row directly and land back on
 * `/connections?connected=<system>`. A deployment that never set `{PREFIX}_CLIENT_ID`/`{PREFIX}_CLIENT_SECRET`
 * gets `503 oauth_not_configured` from the very first click, rendered inline rather than a console 500.
 *
 * **HMRC MTD VAT** still has no self-serve `/authorize`: its consent flow is scoped to one VAT number at a
 * time and is reachable only from a specific migration (`HmrcConsentForm`, `/migrations/:id/connect/hmrc`),
 * so its tile stays a disabled "coming soon" one rather than a button that would 404 here.
 */
export function AddConnectionScreen(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const systems = useSystems();
  const authorizeXero = useStartXeroAuthorization();
  const authorizeQbo = useStartQboAuthorization();
  const authorizeFreeAgent = useStartFreeAgentAuthorization();
  const authorizeSageBc = useStartSageBcAuthorization();
  const authorizeLuciBook = useStartLuciBookAuthorization();
  const createFake = useCreateFakeConnection();

  const [xeroError, setXeroError] = useState<string | null>(null);

  async function onConnectXero(): Promise<void> {
    setXeroError(null);
    try {
      const authorization = await authorizeXero.mutateAsync({});
      // §B1.5: written before the navigation, because the navigation destroys this page.
      saveDraft({ return_to: "/connections" });
      window.location.assign(authorization.authorization_url);
    } catch (error) {
      setXeroError(describeApiError(error, t("error.unexpected")));
    }
  }

  async function onCreateFake(kind: "source" | "destination"): Promise<void> {
    try {
      await createFake.mutateAsync({
        kind,
        org_id: "demo",
        display_name: kind === "source" ? "Demo source (fake)" : "Demo destination (fake)",
      });
      toast.push(t("connections.fake_created"));
      navigate("/connections");
    } catch (error) {
      toast.push(
        describeApiError(error, t("error.unexpected")),
        isApiError(error) ? error.requestId : null,
        "error",
      );
    }
  }

  return (
    <Page
      breadcrumb={
        <Link className="backlink" to="/connections">
          <ChevronLeft size={14} strokeWidth={2} aria-hidden="true" />
          {t("connections.add_back")}
        </Link>
      }
      title={t("connections.add_title")}
      caption={t("connections.add_intro")}
    >
      <section className="connsection" aria-labelledby="add-systems">
        <div className="section-head">
          <h2 id="add-systems" className="eyebrow">
            {t("connections.systems_heading")}
          </h2>
          <span className="section-head__rule" aria-hidden="true" />
        </div>
        <ul className="conngrid">
          <OAuthConnectTile
            system="lucibook"
            name={t("connections.lucibook_name")}
            blurb={t("connections.lucibook_blurb")}
            connectLabel={t("connections.lucibook_oauth_connect")}
            authorize={authorizeLuciBook}
          />
          <ConnectorTile
            system="xero"
            name={t("connections.xero_name")}
            blurb={t("connections.xero_blurb")}
            connectLabel={t("connections.xero_connect")}
            onConnect={() => void onConnectXero()}
            pending={authorizeXero.isPending}
            error={xeroError}
          />
          <OAuthConnectTile
            system="qbo"
            name={t("connections.qbo_name")}
            blurb={t("connections.qbo_blurb")}
            connectLabel={t("connections.qbo_connect")}
            authorize={authorizeQbo}
          />
          <OAuthConnectTile
            system="freeagent"
            name={t("connections.freeagent_name")}
            blurb={t("connections.freeagent_blurb")}
            connectLabel={t("connections.freeagent_connect")}
            authorize={authorizeFreeAgent}
          />
          <OAuthConnectTile
            system="sage_bc"
            name={t("connections.sagebc_name")}
            blurb={t("connections.sagebc_blurb")}
            connectLabel={t("connections.sagebc_connect")}
            authorize={authorizeSageBc}
          />

          <li className="conn conn--agent">
            <VendorMark system="sage50" size={44} />
            {/* AddConnection.html groups the name row and the blurb 2 px apart inside the 10 px text column. */}
            <div className="conn__text">
              <div className="conn__text">
                <div className="conn__namerow">
                  <h3 className="conn__name">{t("connections.sage50_name")}</h3>
                  <Badge tone="neutral">{t("connections.sage50_badge")}</Badge>
                </div>
                <span className="conn__blurb">{t("connections.sage50_blurb")}</span>
              </div>
              <div className="conn__actions">
                <Button disabled>
                  <Download size={16} strokeWidth={2} aria-hidden="true" />
                  {t("connections.sage50_download")}
                </Button>
                <span className="conn__note">{t("connections.sage50_note")}</span>
              </div>
            </div>
          </li>

          <li className="conn conn--soon">
            <VendorMark system="hmrc" size={44} />
            <div className="conn__text">
              <h3 className="conn__name">{t("connections.hmrc_name")}</h3>
              <span className="conn__blurb">{t("connections.hmrc_blurb")}</span>
            </div>
            <Badge tone="neutral">{t("connections.coming_soon")}</Badge>
          </li>
        </ul>
      </section>

      {systems.data?.dev_mode === true ? (
        <section className="devpanel" aria-labelledby="tile-fake">
          <div className="devpanel__text">
            <div className="devpanel__title">
              <span className="tag tag--accent">{t("connections.fake_tag")}</span>
              <h2 id="tile-fake">{t("connections.fake_name")}</h2>
            </div>
            <span className="conn__blurb">{t("connections.fake_blurb")}</span>
          </div>
          <div className="devpanel__actions">
            <Button onClick={() => void onCreateFake("source")}>{t("connections.fake_source")}</Button>
            <Button onClick={() => void onCreateFake("destination")}>
              {t("connections.fake_destination")}
            </Button>
          </div>
        </section>
      ) : null}
    </Page>
  );
}

/**
 * One connector tile in the grid: 44 px vendor mark, name (h3, 16/600) and blurb, and the Connect action.
 * The visible label is the redesign's short "Connect"; the full "Connect to Xero" name stays on the
 * button's `aria-label` so a screen reader (and the tests) can tell the tiles apart.
 */
function ConnectorTile({
  system,
  name,
  blurb,
  connectLabel,
  onConnect,
  pending,
  error,
}: {
  system: string;
  name: string;
  blurb: string;
  connectLabel: string;
  onConnect: () => void;
  pending: boolean;
  error: string | null;
}): JSX.Element {
  return (
    <li className="conn">
      <VendorMark system={system} size={44} />
      <div className="conn__text">
        <h3 className="conn__name">{name}</h3>
        <span className="conn__blurb">{blurb}</span>
      </div>
      <Button aria-label={connectLabel} onClick={onConnect} disabled={pending}>
        {t("connections.connect_short")}
      </Button>
      {error === null ? null : (
        <div className="conn__error">
          <Banner tone="block">{error}</Banner>
        </div>
      )}
    </li>
  );
}

/** The env var prefix `_require_oauth_creds` checks server-side, for `connections.oauth_not_configured`. */
const OAUTH_ENV_PREFIX = {
  qbo: "QBO",
  freeagent: "FREEAGENT",
  sage_bc: "SAGE",
  lucibook: "LUCIBOOK",
} as const;

type OAuthSystem = keyof typeof OAUTH_ENV_PREFIX;

/**
 * QBO/FreeAgent/Sage BC/LuciBook's tile. Same same-tab handoff as Xero's `onConnectXero` above, minus the
 * draft: none of the four callbacks reads a `redirect_path` or needs one, so there is nothing to write to
 * `sessionStorage` before the navigation destroys this page.
 */
function OAuthConnectTile({
  system,
  name,
  blurb,
  connectLabel,
  authorize,
}: {
  system: OAuthSystem;
  name: string;
  blurb: string;
  connectLabel: string;
  authorize: ReturnType<typeof useStartQboAuthorization>;
}): JSX.Element {
  const [error, setError] = useState<string | null>(null);

  async function onConnect(): Promise<void> {
    setError(null);
    try {
      const authorization = await authorize.mutateAsync({});
      window.location.assign(authorization.authorization_url);
    } catch (caught) {
      if (isApiError(caught) && caught.code === "oauth_not_configured") {
        setError(t("connections.oauth_not_configured", { prefix: OAUTH_ENV_PREFIX[system] }));
      } else {
        setError(describeApiError(caught, t("error.unexpected")));
      }
    }
  }

  return (
    <ConnectorTile
      system={system}
      name={name}
      blurb={blurb}
      connectLabel={connectLabel}
      onConnect={() => void onConnect()}
      pending={authorize.isPending}
      error={error}
    />
  );
}

//  LuciBook's manual API key form (`LuciBookTile`/`validate`/`LuciBookConnectForm`, and the `POST
//  /api/v1/connections/lucibook` `client_credentials` mutation they drove) is REMOVED - 2026-09-24 owner
//  decision. LuciBook's tile above is now a plain `OAuthConnectTile`, exactly like QBO/FreeAgent/Sage BC:
//  the stale "OAuth2 client credentials cannot work here" blocker this comment used to cite predates
//  LuciBook's own `LUCIBOOK_SCOPES` gaining `migration:read`/`migration:write`
//  (`connectors/lucibook/oauth.py`), which is what makes an authorization-code token drive the migration
//  API now.
